import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { downloadCSV } from "../../lib/admin-helpers";
import { Download } from "lucide-react";
import { AdminPhoneActions } from "../../components/AdminPhoneActions";
import { formatPhoneForDisplay } from "../../lib/phone";

export default function AdminInquiries() {
  const [rows, setRows] = useState([]);

  const load = () => api.get("/admin/inquiries").then((r) => setRows(r.data));

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-inquiries-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Inquiries</h1>
          <p className="text-sm text-slate-500">Contact and quote requests from the website.</p>
        </div>
        <button
          onClick={() => downloadCSV("/admin/inquiries.csv", "inquiries.csv")}
          data-testid="export-inquiries-csv"
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="mt-6 grid gap-4">
        {rows.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            No inquiries yet.
          </div>
        )}
        {rows.map((i) => (
          <article key={i.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`inquiry-${i.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-sky-700">{i.kind}</p>
                <h3 className="mt-1 font-heading text-lg font-semibold">
                  {i.name} {i.company ? <span className="text-slate-500 font-normal">· {i.company}</span> : null}
                </h3>
                <p className="text-sm text-slate-600 mt-0.5">
                  <a href={`mailto:${i.email}`} className="underline">{i.email}</a>
                  {i.phone && <> · {formatPhoneForDisplay(i.phone)}</>}
                </p>
                {i.preferred_contact_method && <p className="text-xs text-slate-500 mt-1 capitalize">Prefers {i.preferred_contact_method}</p>}
                <AdminPhoneActions
                  phone={i.phone}
                  preferredContactMethod={i.preferred_contact_method}
                  testIdPrefix={`inquiry-phone-${i.id}`}
                />
                {i.service_type && <p className="text-xs text-slate-500 mt-1">Service: {i.service_type}</p>}
                {i.duration && <p className="text-xs text-slate-500">Duration: {i.duration} · Start: {i.start_date}</p>}
                {i.estimated_usage && <p className="text-xs text-slate-500">Usage: {i.estimated_usage}</p>}
                {i.message && <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{i.message}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-slate-400">{new Date(i.created_at).toLocaleString()}</p>
                <button onClick={() => remove(i.id)} data-testid={`delete-inquiry-${i.id}`} className="mt-2 text-xs text-red-600 hover:underline">Delete</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
